/**
 * Hook to compute onboarding completion status.
 *
 * This hook reads from existing data sources (farms, fields, animal groups)
 * to determine which onboarding steps have been completed.
 */

import { useCurrentFarm, useUserFarms } from "model";
import { useFields } from "model/useFieldQueries";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ONBOARDING_DISMISSED_KEY,
  ONBOARDING_STEPS,
  OnboardingStepId,
} from "./onboardingSteps";

export interface OnboardingStepState {
  id: OnboardingStepId;
  completed: boolean;
  /** Whether this step can be acted on now (prerequisites met) */
  actionable: boolean;
}

/**
 * Input for computing onboarding step states.
 * Extracted for testability.
 */
export interface OnboardingComputeInput {
  farmsCount: number;
  currentFarmFieldsCount: number;
  currentFarmAnimalGroupsCount: number;
  hasAssignedField: boolean;
}

/**
 * Pure function to compute step states.
 * Exported for testing.
 */
export function computeOnboardingSteps(
  input: OnboardingComputeInput
): OnboardingStepState[] {
  const hasFarm = input.farmsCount > 0;
  const hasField = input.currentFarmFieldsCount > 0;
  const hasAnimalGroup = input.currentFarmAnimalGroupsCount > 0;
  const hasAssignedField = input.hasAssignedField;

  return ONBOARDING_STEPS.map((step) => {
    let completed = false;
    let actionable = false;

    switch (step.id) {
      case "create-farm":
        completed = hasFarm;
        actionable = !completed;
        break;
      case "add-field":
        completed = hasField;
        actionable = hasFarm && !completed;
        break;
      case "create-animal-group":
        completed = hasAnimalGroup;
        actionable = hasFarm && !completed;
        break;
      case "assign-field":
        completed = hasAssignedField;
        actionable = hasField && hasAnimalGroup && !completed;
        break;
    }

    return { id: step.id, completed, actionable };
  });
}

/**
 * Get the first incomplete step from step states.
 * Exported for testing.
 */
export function getFirstIncompleteStep(
  steps: OnboardingStepState[]
): OnboardingStepId | null {
  const incomplete = steps.find((s) => !s.completed);
  return incomplete?.id ?? null;
}

/**
 * Check if any field has an assigned animal group.
 * Exported for testing.
 */
export function hasFieldWithAssignment(
  fields: Array<{ animalGroup?: string; animalGroupNow?: string }>
): boolean {
  return fields.some((field) => field.animalGroup || field.animalGroupNow);
}

export interface OnboardingState {
  /** Whether all steps are completed */
  isComplete: boolean;
  /** Whether the user has dismissed the onboarding checklist */
  isDismissed: boolean;
  /** Total number of steps */
  totalSteps: number;
  /** Number of completed steps */
  completedSteps: number;
  /** Step states with completion info */
  steps: OnboardingStepState[];
  /** Whether data is still loading */
  isLoading: boolean;
  /** Dismiss the onboarding checklist */
  dismiss: () => void;
  /** Reset dismissal (show checklist again) */
  resetDismissal: () => void;
  /** Get the first incomplete step */
  firstIncompleteStep: OnboardingStepId | null;
}

/**
 * Hook to get the current onboarding state.
 *
 * Computes completion status by checking:
 * - create-farm: user has at least one farm
 * - add-field: current farm has at least one field
 * - create-animal-group: current farm has at least one animal group
 * - assign-field: at least one field has an assigned animal group
 */
export function useOnboardingState(): OnboardingState {
  // Get user's farms
  const { data: farms = [], isLoading: farmsLoading } = useUserFarms();

  // Get current farm
  const { data: currentFarm, isLoading: currentFarmLoading } = useCurrentFarm();

  // Get fields for current farm
  const { data: fields = [], isLoading: fieldsLoading } = useFields();

  // Local state for dismissal
  const [isDismissed, setIsDismissed] = useState(() => {
    if (typeof window === "undefined") return false;
    return localStorage.getItem(ONBOARDING_DISMISSED_KEY) === "true";
  });

  // Listen for storage changes (in case another tab changes it)
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === ONBOARDING_DISMISSED_KEY) {
        setIsDismissed(e.newValue === "true");
      }
    };
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  const dismiss = useCallback(() => {
    localStorage.setItem(ONBOARDING_DISMISSED_KEY, "true");
    setIsDismissed(true);
  }, []);

  const resetDismissal = useCallback(() => {
    localStorage.removeItem(ONBOARDING_DISMISSED_KEY);
    setIsDismissed(false);
  }, []);

  // Compute step completion and actionability using extracted pure function
  const steps = useMemo(
    () =>
      computeOnboardingSteps({
        farmsCount: farms.length,
        currentFarmFieldsCount: currentFarm?.fields?.length ?? 0,
        currentFarmAnimalGroupsCount: currentFarm?.animalGroups?.length ?? 0,
        hasAssignedField: hasFieldWithAssignment(fields),
      }),
    [farms.length, currentFarm, fields]
  );

  const completedSteps = steps.filter((s) => s.completed).length;
  const isComplete = completedSteps === ONBOARDING_STEPS.length;
  // Wait for all onboarding inputs to finish loading before rendering checklist.
  // This prevents a brief flash of incomplete steps for already-onboarded users.
  const isLoading = farmsLoading || currentFarmLoading || fieldsLoading;

  const firstIncompleteStep = useMemo(
    () => getFirstIncompleteStep(steps),
    [steps]
  );

  return {
    isComplete,
    isDismissed,
    totalSteps: ONBOARDING_STEPS.length,
    completedSteps,
    steps,
    isLoading,
    dismiss,
    resetDismissal,
    firstIncompleteStep,
  };
}
